import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * AdminSurveyorManagement — admin-only CRUD for the `surveyors` collection.
 * iter52. Each surveyor has: name, email (= login username), password,
 * optional calendly_url. Soft-delete via DELETE → sets active=false.
 */
export function AdminSurveyorManagement({ token }) {
  const [surveyors, setSurveyors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', calendly_url: '' });
  const [error, setError] = useState('');

  const fetchSurveyors = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/surveyors-manage`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true,
      });
      setSurveyors(data.surveyors || []);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load surveyors');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchSurveyors(); }, [fetchSurveyors]);

  const reset = () => {
    setForm({ name: '', email: '', password: '', calendly_url: '' });
    setEditingId(null);
    setShowAddForm(false);
    setError('');
  };

  const handleSave = async () => {
    setError('');
    if (!form.name.trim() || !form.email.trim()) {
      setError('Name and email are required');
      return;
    }
    if (!editingId && form.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    const body = {
      name: form.name.trim(),
      email: form.email.trim(),
      calendly_url: form.calendly_url.trim(),
    };
    if (form.password) body.password = form.password;
    try {
      if (editingId) {
        await axios.put(`${API_URL}/api/admin/surveyors-manage/${editingId}`, body, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true,
        });
      } else {
        await axios.post(`${API_URL}/api/admin/surveyors-manage`, body, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true,
        });
      }
      reset();
      fetchSurveyors();
    } catch (err) {
      setError(err?.response?.data?.error || 'Save failed');
    }
  };

  const handleEdit = (s) => {
    setEditingId(s.surveyor_id);
    setForm({
      name: s.name || '',
      email: s.email || '',
      password: '',
      calendly_url: s.calendly_url || '',
    });
    setShowAddForm(true);
    setError('');
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Deactivate surveyor "${s.name}"?\n\nThey will no longer be able to log in. Existing assignments are preserved.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/surveyors-manage/${s.surveyor_id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true,
      });
      fetchSurveyors();
    } catch (err) {
      setError(err?.response?.data?.error || 'Deactivate failed');
    }
  };

  return (
    <div data-testid="surveyor-management">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-navy)', margin: 0 }}>Surveyors</h2>
          <p style={{ fontSize: 13, color: 'var(--color-muted)', margin: '4px 0 0' }}>
            Manage who can log in to the QS portal. Email serves as their login username.
          </p>
        </div>
        {!showAddForm && (
          <button
            data-testid="surveyor-add-btn"
            onClick={() => { reset(); setShowAddForm(true); }}
            style={{
              background: 'var(--color-navy)', color: '#fff', border: 'none',
              padding: '10px 18px', borderRadius: 8, fontWeight: 700, cursor: 'pointer',
            }}
          >
            + Add surveyor
          </button>
        )}
      </div>

      {showAddForm && (
        <div data-testid="surveyor-form" style={{
          background: '#F8FAFC', border: '1px solid var(--color-border)',
          borderRadius: 10, padding: 18, marginBottom: 18,
        }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-navy)', margin: '0 0 12px' }}>
            {editingId ? 'Edit surveyor' : 'Add a new surveyor'}
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
            <Field label="Full name" testid="surveyor-form-name"
                   value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
            <Field label="Email (used as username)" testid="surveyor-form-email"
                   type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
            <Field label={editingId ? 'New password (leave blank to keep)' : 'Password (min 8 chars)'}
                   testid="surveyor-form-password" type="password"
                   value={form.password} onChange={(v) => setForm({ ...form, password: v })} />
            <Field label="Calendly URL (optional)" testid="surveyor-form-calendly"
                   placeholder="https://calendly.com/..."
                   value={form.calendly_url} onChange={(v) => setForm({ ...form, calendly_url: v })} />
          </div>
          {error && (
            <div data-testid="surveyor-form-error"
                 style={{ marginTop: 10, color: '#B91C1C', fontSize: 13 }}>{error}</div>
          )}
          <div style={{ marginTop: 14, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button data-testid="surveyor-form-cancel" onClick={reset}
                    style={{ background: '#fff', border: '1px solid var(--color-border)', padding: '8px 14px', borderRadius: 6, cursor: 'pointer' }}>
              Cancel
            </button>
            <button data-testid="surveyor-form-save" onClick={handleSave}
                    style={{ background: '#10B981', color: '#fff', border: 'none', padding: '9px 16px', borderRadius: 6, fontWeight: 700, cursor: 'pointer' }}>
              {editingId ? 'Save changes' : 'Create surveyor'}
            </button>
          </div>
        </div>
      )}

      {error && !showAddForm && (
        <div style={{ marginBottom: 14, color: '#B91C1C', fontSize: 13 }}>{error}</div>
      )}

      <div style={{ background: '#fff', border: '1px solid var(--color-border)', borderRadius: 10, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#F8FAFC', textAlign: 'left' }}>
              <th style={th}>Name</th>
              <th style={th}>Email / Username</th>
              <th style={th}>Calendly</th>
              <th style={th}>Status</th>
              <th style={{ ...th, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} style={{ padding: 20, textAlign: 'center', color: 'var(--color-muted)' }}>Loading…</td></tr>
            )}
            {!loading && surveyors.length === 0 && (
              <tr><td colSpan={5} style={{ padding: 20, textAlign: 'center', color: 'var(--color-muted)' }}>
                No surveyors yet. Click <strong>+ Add surveyor</strong> to create one.
              </td></tr>
            )}
            {surveyors.map((s) => (
              <tr key={s.surveyor_id} data-testid={`surveyor-row-${s.surveyor_id}`} style={{ borderTop: '1px solid var(--color-border)' }}>
                <td style={td}><strong>{s.name}</strong></td>
                <td style={td}>{s.email}</td>
                <td style={td}>
                  {s.calendly_url
                    ? <a href={s.calendly_url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563EB' }}>Link</a>
                    : <span style={{ color: 'var(--color-muted)' }}>—</span>}
                </td>
                <td style={td}>
                  {s.active
                    ? <span style={{ color: '#059669', fontWeight: 700 }}>Active</span>
                    : <span style={{ color: '#9CA3AF' }}>Inactive</span>}
                </td>
                <td style={{ ...td, textAlign: 'right' }}>
                  <button data-testid={`surveyor-edit-${s.surveyor_id}`} onClick={() => handleEdit(s)}
                          style={btn}>Edit</button>
                  {s.active && (
                    <button data-testid={`surveyor-delete-${s.surveyor_id}`}
                            onClick={() => handleDelete(s)}
                            style={{ ...btn, color: '#B91C1C', marginLeft: 6 }}>
                      Deactivate
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = 'text', placeholder }) {
  return (
    <label style={{ display: 'block' }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-muted)', marginBottom: 4 }}>{label}</div>
      <input
        data-testid={testid}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%', padding: '9px 11px', border: '1px solid var(--color-border)',
          borderRadius: 6, fontSize: 13, fontFamily: 'inherit',
        }}
      />
    </label>
  );
}

const th = { padding: '11px 14px', fontSize: 11, fontWeight: 700, color: 'var(--color-muted)', textTransform: 'uppercase', letterSpacing: 0.4 };
const td = { padding: '11px 14px' };
const btn = { background: '#fff', border: '1px solid var(--color-border)', padding: '5px 11px', borderRadius: 5, fontSize: 12, cursor: 'pointer' };
